import type { AppError } from '@aibi/shared';
import { Button, EmptyState, Spinner } from '@aibi/ui';
import { CircleAlert, TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useServices } from '../app/services';
import { WorkspaceShell } from './components/shell/WorkspaceShell';
import { openWorkspace, type OpenedWorkspace } from './services/open-workspace';
import type { SemanticModelSummary } from './services/semantic-model-catalog';
import { useUiDispatch, useUiState } from './state/ui-state';
import { WorkspaceProvider } from './state/WorkspaceProvider';

type CatalogState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly models: readonly SemanticModelSummary[];
      /** El servidor no respondio: solo se listan los modelos ya conocidos y los demo. */
      readonly unavailable: AppError | null;
    }
  | { readonly status: 'error'; readonly error: AppError };

type Opened =
  | { readonly modelId: string; readonly ok: true; readonly value: OpenedWorkspace }
  | { readonly modelId: string; readonly ok: false; readonly error: AppError };

/**
 * Punto de entrada de la experiencia analitica: obtiene los modelos disponibles, abre el
 * analisis del modelo activo (borrador o inicial) y monta el workspace con su estado.
 */
export function AnalysisWorkspace() {
  const { modelCatalog, workspaceRepository } = useServices();
  const uiDispatch = useUiDispatch();
  const ui = useUiState();
  const [catalog, setCatalog] = useState<CatalogState>({ status: 'loading' });
  const [requestedModelId, setRequestedModelId] = useState<string | null>(ui.requestedModelId);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void modelCatalog.list().then((result) => {
      if (cancelled) return;
      setCatalog(
        result.ok
          ? {
              status: 'ready',
              models: result.value,
              unavailable: modelCatalog.unavailable?.() ?? null,
            }
          : { status: 'error', error: result.error },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [modelCatalog, attempt]);

  // Un modelo pedido explicitamente (p. ej. desde Conexiones) que no esta en el catalogo no
  // se sustituye por otro: se informa, para no abrir datos de demostracion en su lugar.
  const missingModelId =
    catalog.status === 'ready' &&
    requestedModelId !== null &&
    !catalog.models.some((model) => model.id === requestedModelId)
      ? requestedModelId
      : null;
  const modelId =
    catalog.status !== 'ready' || missingModelId !== null
      ? null
      : (requestedModelId ?? catalog.models[0]?.id ?? null);

  useEffect(() => {
    if (modelId === null) return;
    let cancelled = false;
    void openWorkspace({ modelCatalog, workspaceRepository }, modelId).then((result) => {
      if (cancelled) return;
      setOpened(
        result.ok
          ? { modelId, ok: true, value: result.value }
          : { modelId, ok: false, error: result.error },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [modelId, modelCatalog, workspaceRepository, attempt]);

  const openedModelId = opened?.ok === true ? opened.value.model.id : null;
  useEffect(() => {
    uiDispatch({ type: 'set-active-model', modelId: openedModelId });
  }, [openedModelId, uiDispatch]);

  const reload = () => {
    setAttempt((value) => value + 1);
  };

  if (catalog.status === 'error') return <LoadError error={catalog.error} onReload={reload} />;
  if (catalog.status === 'ready' && missingModelId !== null) {
    return (
      <div className="flex-1">
        <EmptyState
          tone="error"
          icon={<CircleAlert />}
          title="El modelo solicitado no esta disponible"
          description={
            catalog.unavailable?.message ??
            `El modelo "${missingModelId}" no esta publicado en el servidor.`
          }
          action={
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={reload}>
                Volver a cargar
              </Button>
              {catalog.models.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setRequestedModelId(null);
                  }}
                >
                  Abrir otro modelo
                </Button>
              )}
            </div>
          }
        />
      </div>
    );
  }
  if (catalog.status === 'ready' && catalog.models.length === 0) {
    return (
      <div className="flex-1">
        <EmptyState
          title="No hay modelos semanticos disponibles"
          description="Publique un modelo para comenzar a analizar."
        />
      </div>
    );
  }
  if (opened?.modelId !== modelId || catalog.status === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Abriendo analisis" />
      </div>
    );
  }
  if (!opened.ok) return <LoadError error={opened.error} onReload={reload} />;

  return (
    <>
      {catalog.unavailable && (
        <ServerUnavailableBanner error={catalog.unavailable} onReload={reload} />
      )}
      <WorkspaceProvider
        key={opened.value.model.id}
        model={opened.value.model}
        initialDocument={opened.value.document}
      >
        <WorkspaceShell models={catalog.models} onModelChange={setRequestedModelId} />
      </WorkspaceProvider>
    </>
  );
}

function ServerUnavailableBanner({
  error,
  onReload,
}: {
  readonly error: AppError;
  readonly onReload: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex shrink-0 items-center gap-2 border-b border-warning/40 bg-warning/10 px-3 py-1.5 text-xs text-warning"
    >
      <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        No se pudieron obtener los modelos publicados del servidor ({error.message}). Solo se
        muestran los modelos ya cargados y los de demostracion.
      </span>
      <Button size="sm" variant="outline" className="h-6 text-xs" onClick={onReload}>
        Reintentar
      </Button>
    </div>
  );
}

function LoadError({
  error,
  onReload,
}: {
  readonly error: AppError;
  readonly onReload: () => void;
}) {
  return (
    <div className="flex-1">
      <EmptyState
        tone="error"
        icon={<CircleAlert />}
        title="No se pudo abrir el analisis"
        description={error.message}
        action={
          <Button size="sm" variant="outline" onClick={onReload}>
            Volver a cargar
          </Button>
        }
      />
    </div>
  );
}
